import { useEffect, useState } from "react";
import { Link } from "wouter";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { PhysioLayout } from "@/components/layout/physio-layout";
import { OTLayout } from "@/components/layout/ot-layout";
import { AlertTriangle, ClipboardList, Loader2, Plus } from "lucide-react";

type LayoutComponent = React.ComponentType<{ children: React.ReactNode; title: string }>;

function getRoleLayout(role: string | null): LayoutComponent {
  switch (role) {
    case "doctor": return DoctorLayout;
    case "pharmacist": return PharmacistLayout;
    case "physio": return PhysioLayout;
    case "ot": return OTLayout;
    default: return NurseLayout;
  }
}

interface SafetyReport {
  id: number;
  referenceNumber: string;
  concernType: string;
  description: string;
  pageOrFeature: string | null;
  status: string;
  createdAt: string;
}

const CONCERN_TYPE_LABELS: Record<string, string> = {
  "clinical-content-error": "Clinical content error",
  "ai-response-concern": "AI response concern",
  "data-issue": "Data issue",
  "other": "Other",
};

const STATUS_CONFIG: Record<string, { label: string; classes: string }> = {
  pending: {
    label: "Pending",
    classes: "bg-amber-50 text-amber-700 border border-amber-200",
  },
  investigating: {
    label: "Investigating",
    classes: "bg-blue-50 text-blue-700 border border-blue-200",
  },
  resolved: {
    label: "Resolved",
    classes: "bg-teal-50 text-teal-700 border border-teal-200",
  },
  closed: {
    label: "Closed",
    classes: "bg-slate-100 text-slate-600 border border-slate-200",
  },
};

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? {
    label: status,
    classes: "bg-slate-100 text-slate-600 border border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${config.classes}`}>
      {config.label}
    </span>
  );
}

export default function HcpSafetyReportsPage() {
  const role = localStorage.getItem("rheumhub_role");
  const Layout = getRoleLayout(role);

  const [reports, setReports] = useState<SafetyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/hcp/safety-reports")
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `Unexpected error (${res.status})`);
        }
        return res.json() as Promise<SafetyReport[]>;
      })
      .then((data) => { setReports(data); setLoading(false); })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Could not load your reports.");
        setLoading(false);
      });
  }, []);

  return (
    <Layout title="My Safety Reports">
      <div className="max-w-2xl mx-auto space-y-6">

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="bg-amber-50 p-2.5 rounded-xl shrink-0">
              <ClipboardList className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">My Safety Reports</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                View the current status of safety concerns you have submitted.
              </p>
            </div>
          </div>
          <Link
            href="/hcp/safety-report"
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors shrink-0"
          >
            <Plus className="h-4 w-4" />
            New report
          </Link>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your reports…
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {!loading && !error && reports.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-slate-500">No reports yet</p>
            <p className="text-sm mt-1">Reports you submit will appear here with their current status.</p>
            <Link
              href="/hcp/safety-report"
              className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-amber-600 hover:text-amber-700 underline"
            >
              Submit your first report
            </Link>
          </div>
        )}

        {!loading && !error && reports.length > 0 && (
          <ul className="space-y-3">
            {reports.map((report) => (
              <li
                key={report.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-bold text-slate-800 tracking-wide">
                      {report.referenceNumber}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {CONCERN_TYPE_LABELS[report.concernType] ?? report.concernType}
                      {report.pageOrFeature ? ` · ${report.pageOrFeature}` : ""}
                    </p>
                  </div>
                  <StatusBadge status={report.status} />
                </div>

                <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                  {report.description}
                </p>

                <p className="text-xs text-slate-400">
                  Submitted{" "}
                  {new Date(report.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </li>
            ))}
          </ul>
        )}

        <p className="text-xs text-center text-slate-400">
          Reports are investigated in line with DCB0129 clinical safety procedures.
          Contact <span className="font-medium">safety@medantixhealth.co.uk</span> if you have an urgent concern.
        </p>
      </div>
    </Layout>
  );
}
